<template>
  <div>
    <!-- Main Navbar -->
    <nav
      class="fixed top-0 left-0 w-full z-40 h-[60px] px-4 md:px-8 flex items-center justify-between bg-gradient-to-r from-[#1D1D1D] to-[#313B44]"
    >
      <!-- Left Section -->
      <div class="flex items-center gap-4 lg:pl-[280px]">
        <!-- Mobile Menu Icon -->
        <button @click="toggleMobileMenu" class="lg:hidden text-white focus:outline-none">
          <svg
            v-if="!mobileMenuOpen"
            xmlns="http://www.w3.org/2000/svg"
            class="h-7 w-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16m-7 6h7"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            class="h-7 w-7"
          >
            <path
              fill-rule="evenodd"
              d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
              clip-rule="evenodd"
            />
          </svg>
        </button>

        <!-- Twitch Logo (Desktop Only) -->
        <a
          href="https://www.kick.com/ballyslots"
          target="_blank"
          rel="noopener noreferrer"
          class="hidden lg:block"
        >
          <img src="@/assets/watch-kick.png" alt="Kick" class="w-[150px] h-auto" />
        </a>
      </div>

      <!-- Center: Mobile Logo -->
      <div class="lg:hidden absolute left-1/2 transform -translate-x-1/2">
        <img
          src="@/assets/ballyboy-logo-mobile.png"
          alt="Bally Boy"
          class="h-[33px] sm:h-[35px] object-contain"
        />
      </div>

      <!-- Right Section -->
      <div class="flex items-center gap-4">
        <!-- Sign In Button (Desktop Only) -->
        <button
          v-if="!isUserLoggedIn"
          @click="handleLogin()"
          :disabled="!canLogin"
          class="hidden lg:flex px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Sign In
        </button>

        <!-- Profile Dropdown -->
        <div class="block ml-6 relative" v-if="isUserLoggedIn">
          <div
            class="flex items-center cursor-pointer gap-2"
            @click="toggleProfileDropdown"
          >
            <!-- Profile Image -->
            <div class="relative w-10 h-10 rounded-full border-2 border-white">
              <img
                :src="$store.state.avatar || require('@/assets/navbar-avatar.png')"
                alt="User Avatar"
                class="w-full h-full rounded-full object-cover"
              />
            </div>

            <!-- Arrow Icon -->
            <svg
              class="w-4 h-4 text-white transition-transform"
              :class="{ 'rotate-180': profileDropdown }"
              fill="white"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd"
              ></path>
            </svg>
          </div>

          <!-- Dropdown Menu -->
          <div
            v-if="profileDropdown"
            class="absolute right-0 mt-2 w-48 bg-[#0E0F15] text-white rounded-lg shadow-lg z-50 border border-white"
          >
            <!-- Profile -->
            <a
              href="/profile"
              class="flex items-center gap-2 px-4 py-3 rounded-t-[10px] hover:bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44]"
            >
              <img
                src="@/assets/icon-user.avif"
                class="h-6 w-6 object-contain"
                alt="user icon"
              />
              <span class="text-white ml-2">Profile</span>
            </a>

            <!-- Logout -->
            <button
              @click="handleLogout"
              class="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-[#0E0F15] rounded-b-lg hover:hover:bg-gradient-to-r from-[#1D1D1D] via-[#1D1D1D] to-[#313B44]"
            >
              <img
                src="@/assets/icon-logout.svg"
                class="h-6 w-6 object-contain"
                alt="logout icon"
              />
              <span class="text-white ml-2">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </nav>

    <!-- Mobile Sidebar -->
    <transition name="slide">
      <div
        v-if="mobileMenuOpen"
        class="sidebar fixed bg-[#21272E] w-60 md:w-80 h-screen overflow-y-auto z-40 mt-[60px]"
        @click.stop
      >
        <div class="flex flex-col uppercase" style="font-family: Oxanium, sans-serif">
          <router-link
            @click="mobileMenuOpen = false"
            to="/"
            class="text-white flex items-center py-5 hover:bg-[#D9D9D9] hover:bg-opacity-10"
          >
            <div class="flex flex-row items-center ml-4 gap-2">
              <img src="@/assets/home.png" alt="home" class="w-6" />
              <p class="tracking-widest font-bold text-xs ml-2">Home</p>
            </div>
          </router-link>

          <!-- King of the Hill Menu Item -->
          <router-link
            @click="mobileMenuOpen = false"
            to="/king-of-the-hill"
            class="text-white flex items-center py-5 hover:bg-[#D9D9D9] hover:bg-opacity-10 cursor-pointer"
          >
            <div class="flex flex-row items-center ml-4 gap-2">
              <img src="@/assets/crown.png" alt="crown" class="w-6 mb-0.5" />
              <p class="tracking-widest font-bold text-xs ml-2">King of the Hill</p>
            </div>
          </router-link>

          <!-- Bounties Menu Item -->
          <router-link
            @click="mobileMenuOpen = false"
            to="/bounties"
            class="text-white flex items-center py-5 hover:bg-[#D9D9D9] hover:bg-opacity-10 cursor-pointer"
          >
            <div class="flex flex-row items-center ml-4 gap-2">
              <img src="@/assets/game-console.png" alt="like" class="w-6 mb-0.5" />
              <p class="tracking-widest font-bold text-xs ml-2">Bounties</p>
            </div>
          </router-link>

          <!-- Monthly Bonus Dropdown Menu -->
          <div
            @click="toggleDropdown('monthlyBonus')"
            class="text-white flex items-center py-5 hover:bg-[#D9D9D9] hover:bg-opacity-10 cursor-pointer"
          >
            <div class="flex flex-row items-center ml-4 gap-2">
              <img
                src="@/assets/dollar-circle.png"
                alt="monthly bonus"
                class="w-6 mb-0.5"
              />
              <p class="tracking-widest font-bold text-xs ml-2">Monthly Bonus</p>
              <span
                class="ml-auto mr-4 transform transition-transform"
                :class="{ 'rotate-180': dropdowns.monthlyBonus }"
              >
                <img class="ml-1" src="@/assets/dropdown-arrow.png" />
              </span>
            </div>
          </div>

          <transition name="slides">
            <div
              v-if="dropdowns.monthlyBonus"
              class="ml-6 mt-1 text-xs font-bold text-white"
            >
              <router-link
                @click="mobileMenuOpen = false"
                to="/monthly-bonus?type=metaspins"
                class="block py-4 hover:bg-[#D9D9D9] hover:bg-opacity-10"
              >
                <div class="flex flex-row items-center ml-4 gap-2">
                  <img src="@/assets/dollar-circle.png" class="w-4 mb-0.5" />
                  <p class="tracking-widest font-bold text-xs">Metaspins</p>
                </div>
              </router-link>
              <router-link
                @click="mobileMenuOpen = false"
                to="/monthly-bonus?type=dailyspins"
                class="block py-4 hover:bg-[#D9D9D9] hover:bg-opacity-10"
              >
                <div class="flex flex-row items-center ml-4 gap-2">
                  <img src="@/assets/dollar-circle.png" class="w-4 mb-0.5" />
                  <p class="tracking-widest font-bold text-xs">Dailyspins</p>
                </div>
              </router-link>
            </div>
          </transition>

          <router-link
            @click="mobileMenuOpen = false"
            to="/milestones"
            class="text-white flex items-center py-5 hover:bg-[#D9D9D9] hover:bg-opacity-10"
          >
            <div class="flex flex-row items-center ml-4 gap-2">
              <img src="@/assets/flag.png" alt="flag" class="w-6" />
              <p class="tracking-widest font-bold text-xs ml-2">Milestones</p>
            </div>
          </router-link>

          <div
            @click="toggleDropdown('leaderboards')"
            class="text-white flex items-center py-5 hover:bg-[#D9D9D9] hover:bg-opacity-10 cursor-pointer"
          >
            <div class="flex flex-row items-center ml-4 gap-2">
              <img src="@/assets/cup.png" alt="crown" class="w-6 mb-0.5" />
              <p class="tracking-widest font-bold text-xs ml-2">Leaderboards</p>
              <span
                class="ml-auto mr-4 transform transition-transform"
                :class="{ 'rotate-180': dropdowns.leaderboards }"
              >
                <img class="ml-1" src="@/assets/dropdown-arrow.png" />
              </span>
            </div>
          </div>

          <transition name="slides">
            <div
              v-if="dropdowns.leaderboards"
              class="ml-6 mt-1 text-xs font-bold text-white"
            >
              <router-link
                @click="mobileMenuOpen = false"
                to="/leaderboards?type=metaspins"
                class="block py-4 hover:bg-[#D9D9D9] hover:bg-opacity-10"
              >
                <div class="flex flex-row items-center ml-4 gap-2">
                  <img src="@/assets/cup.png" class="w-4 mb-0.5" />
                  <p class="tracking-widest font-bold text-xs">Metaspins</p>
                </div>
              </router-link>
              <!-- <router-link
                @click="mobileMenuOpen = false"
                to="/leaderboards?type=empiredrop"
                class="block py-4 hover:bg-[#D9D9D9] hover:bg-opacity-10"
              >
                <div class="flex flex-row items-center ml-4 gap-2">
                  <img src="@/assets/cup.png" class="w-4 mb-0.5" />
                  <p class="tracking-widest font-bold text-xs">Empire Drop</p>
                </div>
              </router-link> -->
              <router-link
                @click="mobileMenuOpen = false"
                to="/leaderboards?type=dailyspins"
                class="block py-4 hover:bg-[#D9D9D9] hover:bg-opacity-10"
              >
                <div class="flex flex-row items-center ml-4 gap-2">
                  <img src="@/assets/cup.png" class="w-4 mb-0.5" />
                  <p class="tracking-widest font-bold text-xs">Dailyspins</p>
                </div>
              </router-link>
              <router-link
                @click="mobileMenuOpen = false"
                to="/leaderboards?type=raingg"
                class="block py-4 hover:bg-[#D9D9D9] hover:bg-opacity-10"
              >
                <div class="flex flex-row items-center ml-4 gap-2">
                  <img src="@/assets/cup.png" class="w-4 mb-0.5" />
                  <p class="tracking-widest font-bold text-xs">Rain.GG</p>
                </div>
              </router-link>
              <router-link
                @click="mobileMenuOpen = false"
                to="/leaderboards?type=chickengg"
                class="block py-4 hover:bg-[#D9D9D9] hover:bg-opacity-10"
              >
                <div class="flex flex-row items-center ml-4 gap-2">
                  <img src="@/assets/cup.png" class="w-4 mb-0.5" />
                  <p class="tracking-widest font-bold text-xs">Chicken.GG</p>
                </div>
              </router-link>
              <router-link
                @click="mobileMenuOpen = false"
                to="/leaderboards?type=csgogem"
                class="block py-4 hover:bg-[#D9D9D9] hover:bg-opacity-10"
              >
                <div class="flex flex-row items-center ml-4 gap-2">
                  <img src="@/assets/cup.png" class="w-4 mb-0.5" />
                  <p class="tracking-widest font-bold text-xs">Csgogem</p>
                </div>
              </router-link>
            </div>
          </transition>

          <router-link
            @click="mobileMenuOpen = false"
            to="/store"
            class="text-white flex items-center py-5 hover:bg-[#D9D9D9] hover:bg-opacity-10"
          >
            <div class="flex flex-row items-center ml-4 gap-2">
              <img src="@/assets/shopping.png" class="w-6" alt="shopping" />
              <p class="tracking-widest font-bold text-xs ml-2">Store</p>
            </div>
          </router-link>

          <router-link
            @click="mobileMenuOpen = false"
            to="/giveaways"
            class="text-white flex items-center py-5 hover:bg-[#D9D9D9] hover:bg-opacity-10"
          >
            <div class="flex flex-row items-center ml-4 gap-2">
              <img src="@/assets/gift-nav.png" alt="gift" />
              <p class="tracking-widest font-bold text-xs ml-2">Giveaways</p>
            </div>
          </router-link>

          <router-link
            @click="mobileMenuOpen = false"
            to="/vip"
            class="text-white flex items-center py-5 hover:bg-[#D9D9D9] hover:bg-opacity-10"
          >
            <div class="flex flex-row items-center ml-4 gap-2">
              <img src="@/assets/like.png" alt="gift" />
              <p class="tracking-widest font-bold text-xs ml-2">VIP</p>
            </div>
          </router-link>

          <button
            v-if="!isUserLoggedIn"
            @click="handleLogin()"
            :disabled="!canLogin"
            class="pl-4 py-5 text-xs hover:bg-[#D9D9D9] hover:bg-opacity-10 flex justify-start items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <img src="@/assets/login.png" alt="login" class="w-6" />
            <p class="tracking-widest font-bold uppercase text-white ml-4">Login</p>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  data() {
    return {
      mobileMenuOpen: false,
      dropdowns: { kingOfTheHill: false, leaderboards: false, monthlyBonus: false },
      profileDropdown: false,
    };
  },
  computed: {
    isUserLoggedIn() {
      return this.$store.state.user !== null && this.$store.state.isVerified;
    },
    canLogin() {
      return this.$store.state.ageConfirmed && this.$store.state.responsibilityConfirmed;
    },
  },
  methods: {
    async checkAuthStatus() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      try {
        const token =
          this.$store.state.user?.accessToken ||
          localStorage.getItem("ballyboy-access-token");

        if (!token) {
          console.log("No token found, updating user to null.");
          return this.$store.commit("UPDATE_USER", null); // If no token, invalidate user
        }

        const response = await fetch(`${baseUrl}/api/auth/verifyAccessToken`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        console.log("Auth verification response:", data);

        if (response.ok && data.isValid) {
          console.log("Token is valid, updating Vuex store.");
          this.$store.commit("UPDATE_ISVERIFIED", true);
          this.$store.commit("UPDATE_USER", data.user);
        } else {
          console.log("Token is not valid, invalidating user.");
          this.$store.commit("UPDATE_USER", null);
          this.$store.commit("UPDATE_ISVERIFIED", false);
        }
      } catch (error) {
        console.error("Auth verification failed:", error);
        this.$store.commit("UPDATE_USER", null);
        this.$store.commit("UPDATE_ISVERIFIED", false);
      }
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },
    toggleDropdown(menu) {
      this.dropdowns[menu] = !this.dropdowns[menu];
    },
    toggleProfileDropdown() {
      this.profileDropdown = !this.profileDropdown;
    },
    handleLogin() {
      const loginUrl = `${process.env.VUE_APP_BASE_API}/api/auth/discord`;
      window.location.href = loginUrl;
    },
    handleLogout() {
      // Clear Vuex state
      this.$store.commit("UPDATE_USER", null);

      // Remove local storage items
      localStorage.removeItem("vuex");
      localStorage.removeItem("ballyboy-access-token");

      // Redirect to login/home page
      this.$router.replace("/");

      // Close sidebar if open
      this.isSidebarOpen = false;

      this.checkAuthStatus();
    },
  },
};
</script>

<style>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-in-out;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}

.slide-enter-to,
.slide-leave-from {
  transform: translateX(0);
}
</style>
